import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DASHBOARD_SIGNUP_URL } from "@/lib/cta";

const REFERRAL_STORAGE_KEY = "gerdoo:referral_code";

/**
 * Carries an affiliate `?ref=` code from the marketing site through to the
 * dashboard's signup page.
 *
 * Every signup CTA here is a plain anchor to an absolute dashboard URL, and the
 * site is prerendered at build time, so the code cannot be baked into those
 * hrefs. Instead this rewrites them after hydration whenever a ref is present —
 * one place, covering every CTA on every page, including ones that build the
 * URL by hand. The panel is a different origin, so localStorage here cannot be
 * read there; the query parameter is what actually crosses over.
 */
export function ReferralLinkForwarder() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    let ref = "";
    try {
      ref = new URLSearchParams(window.location.search).get("ref")?.trim() ?? "";
      if (ref) {
        // Remembered so a visitor who lands on /?ref=X and then browses to the
        // pricing page still leaves through a referral link.
        sessionStorage.setItem(REFERRAL_STORAGE_KEY, ref);
      } else {
        ref = sessionStorage.getItem(REFERRAL_STORAGE_KEY) ?? "";
      }
    } catch {
      // Blocked storage: fall back to the current URL alone.
    }
    if (!ref) return;

    const encoded = encodeURIComponent(ref);
    for (const anchor of Array.from(document.querySelectorAll("a[href]"))) {
      const href = anchor.getAttribute("href") ?? "";
      if (!href.startsWith(DASHBOARD_SIGNUP_URL) || href.includes("ref=")) continue;
      anchor.setAttribute(
        "href",
        href + (href.includes("?") ? "&" : "?") + "ref=" + encoded,
      );
    }
    // Re-runs per navigation: each prerendered page brings its own anchors.
  }, [location.pathname, location.search]);

  return null;
}
